import {
  AccountingIcon,
  ApiKeysIcon,
  ApprovalRuleIcon,
  ConditionalTermIcon,
  CrmIcon,
  CustomFieldIcon,
  DunningIcon,
  InvoiceTemplateIcon,
  InvoicesIcon,
  OrgManagementIcon,
  PaymentGatewayIcon,
  QuoteSettingIcon,
  QuoteTemplateIcon,
  RevenueIcon,
  SubscriptionsIcon,
  TeamIcon,
  ValidationRulesIcon,
  WebhookIcon,
  eSignIcon,
} from '@monetize/ui/icons';
import { ReactNode, useEffect, useMemo, useState, type JSX } from 'react';
import { BsClipboard2Data } from 'react-icons/bs';
import { FaProjectDiagram, FaSlack } from 'react-icons/fa';
import { IoMdEasel } from 'react-icons/io';
import {
  MdChecklist,
  MdConstruction,
  MdDomain,
  MdEuroSymbol,
  MdNotificationsNone,
  MdOutlineAccountTree,
  MdOutlineAssignment,
  MdOutlineCalculate,
  MdOutlineDynamicForm,
  MdOutlineEditNote,
  MdOutlineMailOutline,
  MdOutlinePaid,
  MdOutlineScience,
  MdOutlineSettings,
  MdOutlineShare,
  MdOutlineSupervisorAccount,
  MdPayment,
} from 'react-icons/md';
import { TbZoomMoney } from 'react-icons/tb';
import {
  MBox,
  MFlex,
  MIcon,
  MPageContainer,
  MPageHeader,
  MText,
  MTooltip,
} from '../../../components/Monetize';
import { ButtonCard } from '../../../components/Monetize/Cards/ButtonCard';
import { LinkCard } from '../../../components/Monetize/Cards/LinkCard';
import { Highlighter } from '../../../components/Monetize/MPageSearchInput/Highlighter';
import { useDocumentHead } from '../../../services/documentHead';
import { useSettingsNavRoutes } from '../SideNavBar/settingsNav';
import { SettingSideNavItemKey } from '../SideNavBar/settingsNavType';
import { SettingSearchInput } from './component/SettingSearchInput';

const titleLeftContentMapping: Record<
  SettingSideNavItemKey,
  (props?: any) => JSX.Element | ReactNode
> = {
  accounting: AccountingIcon,
  apikeys: ApiKeysIcon,
  approvalrules: ApprovalRuleIcon,
  auditlog: MdOutlineAssignment,
  billingpayments: MdPayment,
  billingsettings: TbZoomMoney,
  companysettings: MdOutlineSettings,
  conditionalterms: ConditionalTermIcon,
  crm: CrmIcon,
  currencies: MdEuroSymbol,
  customfields: CustomFieldIcon,
  dealRoom: IoMdEasel,
  domains: MdDomain,
  dunning: DunningIcon,
  esign: eSignIcon,
  guidedquoting: MdOutlineAccountTree,
  integrationlog: BsClipboard2Data,
  invoices: InvoicesIcon,
  invoicetemplates: InvoiceTemplateIcon,
  notifications: MdNotificationsNone,
  onboarding: MdChecklist,
  orgmanagement: OrgManagementIcon,
  paymentgateway: PaymentGatewayIcon,
  pricing: MdOutlinePaid,
  quotesettings: QuoteSettingIcon,
  quoteshare: MdOutlineShare,
  quotetemplates: QuoteTemplateIcon,
  quotingemails: MdOutlineMailOutline,
  recordautomation: MdOutlineDynamicForm,
  revenuerecognition: RevenueIcon,
  slack: FaSlack,
  subscriptions: SubscriptionsIcon,
  tax: MdOutlineCalculate,
  templates: MdOutlineEditNote,
  teams: TeamIcon,
  tenantsettings: MdOutlineSettings,
  usersroles: MdOutlineSupervisorAccount,
  validationrules: ValidationRulesIcon,
  webhooks: WebhookIcon,
  quoteblueprints: FaProjectDiagram,
};

export const SettingsLanding = () => {
  const SETTINGS_ROUTES = useSettingsNavRoutes();
  const { setDocTitle } = useDocumentHead();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSettingsRoutes, setFilteredSettingsRoutes] =
    useState(SETTINGS_ROUTES);

  useEffect(() => {
    setDocTitle('Settings');
  }, []);

  // Filter settings based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSettingsRoutes(SETTINGS_ROUTES);
      return;
    }

    const searchWords = searchTerm
      .toLowerCase()
      .split(' ')
      .filter((word) => word.length > 0);

    const filteredRoutes = SETTINGS_ROUTES.map((parentNavRoute) => {
      const filteredChildren = parentNavRoute.children.filter((child) => {
        const titleMatch = searchWords.some((word) =>
          child.title.toLowerCase().includes(word),
        );
        const descriptionMatch = searchWords.some((word) =>
          child.description.toLowerCase().includes(word),
        );
        return titleMatch || descriptionMatch;
      });

      return {
        ...parentNavRoute,
        children: filteredChildren,
      };
    }).filter((parentNavRoute) => parentNavRoute.children.length > 0);

    setFilteredSettingsRoutes(filteredRoutes);
  }, [SETTINGS_ROUTES, searchTerm]);

  // Calculate total count of filtered results
  const totalResultsCount = useMemo(() => {
    if (!searchTerm.trim()) {
      return 0; // Don't show count when no search
    }
    return filteredSettingsRoutes.reduce(
      (total, section) => total + section.children.length,
      0,
    );
  }, [filteredSettingsRoutes, searchTerm]);

  return (
    <MPageContainer>
      <MPageHeader title="Settings" />

      <MBox mb="4" w="full">
        <SettingSearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          count={totalResultsCount}
        />
      </MBox>

      {filteredSettingsRoutes.map((parentNavRoute) => (
        <MBox w="100%" pb={6} key={parentNavRoute.title}>
          <MText fontWeight="semibold" fontSize="lg" pb={2}>
            {parentNavRoute.title}
          </MText>

          <MFlex gap={6} flexWrap="wrap">
            {parentNavRoute.children.map(
              ({ key, description, route, title, isComingSoon, isInBeta }) =>
                isComingSoon ? (
                  <ButtonCard
                    h="100px"
                    key={title}
                    title={
                      searchTerm ? (
                        <Highlighter
                          textToHighlight={title}
                          searchWords={searchTerm
                            .toLowerCase()
                            .split(' ')
                            .filter((word) => word.length > 0)}
                          fontWeight="semibold"
                          fontSize="md"
                        />
                      ) : (
                        title
                      )
                    }
                    description={
                      searchTerm ? (
                        <Highlighter
                          as="span"
                          textToHighlight={description}
                          searchWords={searchTerm
                            .toLowerCase()
                            .split(' ')
                            .filter((word) => word.length > 0)}
                          color="tGray.darkPurple"
                          fontSize="sm"
                        />
                      ) : (
                        description
                      )
                    }
                    isDisabled
                    titleRightContent={
                      <MFlex
                        alignItems="center"
                        columnGap={1}
                        color="tGray.acGray"
                      >
                        <MIcon as={MdConstruction} color="inherit" />
                        <MText color="inherit">Coming soon</MText>
                      </MFlex>
                    }
                    titleLeftContent={
                      titleLeftContentMapping[key] ? (
                        <MIcon
                          as={titleLeftContentMapping[key]}
                          w={6}
                          h={6}
                          color={isComingSoon ? 'tGray.acGray' : 'tPurple.base'}
                        />
                      ) : null
                    }
                  />
                ) : (
                  <LinkCard
                    h="100px"
                    to={route}
                    key={title}
                    title={
                      searchTerm ? (
                        <Highlighter
                          textToHighlight={title}
                          searchWords={searchTerm
                            .toLowerCase()
                            .split(' ')
                            .filter((word) => word.length > 0)}
                          fontWeight="semibold"
                          fontSize="md"
                        />
                      ) : (
                        title
                      )
                    }
                    description={
                      searchTerm ? (
                        <Highlighter
                          as="span"
                          textToHighlight={description}
                          searchWords={searchTerm
                            .toLowerCase()
                            .split(' ')
                            .filter((word) => word.length > 0)}
                          color="tGray.darkPurple"
                          fontSize="sm"
                        />
                      ) : (
                        description
                      )
                    }
                    titleLeftContent={
                      titleLeftContentMapping[key] ? (
                        <MIcon
                          as={titleLeftContentMapping[key]}
                          w={6}
                          h={6}
                          color="tPurple.base"
                        />
                      ) : null
                    }
                    titleRightContent={
                      isInBeta ? (
                        <MTooltip
                          label="This feature is in early release. Let us know if you have any feedback."
                          placement="top"
                        >
                          <MFlex
                            alignItems="center"
                            bgColor="tPurple.inputBox"
                            pr={2}
                            pl={1}
                            borderRadius={2}
                          >
                            <MIcon
                              as={MdOutlineScience}
                              boxSize={4}
                              color="tPurple.safety"
                            />
                            <MText m={0} color="tPurple.safety" ml={1}>
                              Beta
                            </MText>
                          </MFlex>
                        </MTooltip>
                      ) : null
                    }
                  />
                ),
            )}
          </MFlex>
        </MBox>
      ))}
    </MPageContainer>
  );
};
